/* lib/api/idempotency.js — ключ идемпотентности у создания (контракт idempotency-key, шрам S30).

   ЗАЧЕМ. Ответ на создание сериализуется ПОСЛЕ записи в базу: 502/504 от прокси и 500 после
   commit приходят, когда запись уже лежит, и повтор без ключа даёт вторую такую же (живой QA
   kitchen-ux G2, Н1: дубль меню). Каждая ПОПЫТКА создания несёт свой ключ (uuid v4) в заголовке
   Idempotency-Key; сервер хранит его вместе с созданной записью (пространство + вид записи), и
   повтор той же попытки получает уже созданную запись, а не новую.

   ОТВЕТЫ СЕРВЕРА С КЛЮЧОМ:
   · 201 — создано, тело как без ключа;
   · 200 + idempotent_replay: true — повтор: запись уже была (тело — её текущее состояние);
     для клиента — то же, что 201 (isReplay, withoutReplayMark);
   · 422 idempotency_key_reused + existing — тот же ключ, другое тело (поля поправили после
     сбоя, а первая попытка легла): ничего не создано, existing — запись первой попытки
     (isKeyReused, reusedExisting);
   · 410 idempotency_object_gone — запись первой попытки уже удалена: следующей попытке — НОВЫЙ
     ключ (isKeyGone, KEY_GONE_TEXT — текст сервера на случай, если его нет в ответе);
   · 400 idempotency_key_invalid — кривой заголовок (uuid отсюда таким не бывает).

   ПРАВИЛА КЛИЕНТА. Один ключ — на одну попытку (черновик и лист создания реестра, новая сущность
   редактора, форма цены); все её повторы — автоматический и ручные, в том числе после правки
   полей, — тем же ключом. Неизвестный исход (isKeyedCreateUnknown: ответа нет, 408, ЛЮБОЙ 5xx,
   включая 500) — ОДИН автоматический повтор тем же ключом через RETRY_DELAY_MS
   (retryOnceIfUnknown), дальше — ручной, тем же ключом. Офлайн до отправки (notSent) — исход
   известен: не ушло, повтора нет. Без ключа правило S30 «ни одного автоматического POST при
   неизвестном исходе» остаётся в силе.

   Без React и без window (crypto читается при вызове — тесты его подменяют); от сетевого слоя — только
   пауза перед повтором в счёте «в полёте» (client.trackedPause, S10). */

import { trackedPause } from './client.js';

export const IDEMPOTENCY_HEADER = 'Idempotency-Key';
/** Пауза перед автоматическим повтором попытки с неизвестным исходом. */
export const RETRY_DELAY_MS = 1500;
/** 410 idempotency_object_gone без текста в ответе — слова контракта. */
export const KEY_GONE_TEXT = 'Запись, созданная этой попыткой, уже удалена.';

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

function cryptoApi() {
  try {
    const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
    return c && typeof c === 'object' ? c : null;
  } catch {
    return null;
  }
}

// 16 случайных байт → uuid v4 (RFC 4122: версия 4, вариант 10xx)
function formatV4(bytes) {
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => (b + 0x100).toString(16).slice(1)).join('');
  return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
}

/** Новый ключ попытки — uuid v4: crypto.randomUUID (только в безопасном контексте), иначе
 *  crypto.getRandomValues, в крайнем случае Math.random. Формат одинаков на всех путях. */
export function newIdempotencyKey() {
  const c = cryptoApi();
  if (c && typeof c.randomUUID === 'function') {
    try {
      const id = String(c.randomUUID()).toLowerCase();
      if (UUID_V4.test(id)) return id;
    } catch { /* небезопасный контекст — запасной путь */ }
  }
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    try {
      c.getRandomValues(bytes);
      return formatV4(bytes);
    } catch { /* нет источника — Math.random */ }
  }
  for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return formatV4(bytes);
}

/** Опции запроса (apiFetch / доменных функций: { headers, signal, … }) + заголовок ключа.
 *  X-Workspace-Id из pinWorkspace и прочее остаются; вход не меняется. Ключа нет — опции как
 *  есть (без опций — {}): запрос уходит, как до фичи. */
export function withIdempotencyKey(req, key) {
  if (key == null || key === '') return req == null ? {} : req;
  const base = req && typeof req === 'object' ? req : {};
  return { ...base, headers: { ...(base.headers || {}), [IDEMPOTENCY_HEADER]: String(key) } };
}

const isEnvelope = (v) => !!v && typeof v === 'object' && typeof v.ok === 'boolean'
  && typeof v.status === 'number' && 'data' in v;
const isObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

/** Ответ — повтор уже созданной записи (200, idempotent_replay: true). Тело или конверт. */
export function isReplay(data) {
  const body = isEnvelope(data) ? data.data : data;
  return isObject(body) && body.idempotent_replay === true;
}

/** Тело повтора без метки — в форме ответа создания (для показа «как созданное»). */
export function withoutReplayMark(data) {
  if (!isObject(data) || !('idempotent_replay' in data)) return data;
  const { idempotent_replay: _mark, ...rest } = data;
  return rest;
}

/* Отказ приходит в трёх формах: Error из lib/api/unwrap (code и existing — на ошибке),
   исключение слоя таблицы { status, data } и конверт apiFetch { ok:false, status, data }. */
function codeOf(err) {
  if (!err || typeof err !== 'object') return null;
  if (typeof err.code === 'string') return err.code;
  const d = err.data;
  return isObject(d) && typeof d.code === 'string' ? d.code : null;
}
const statusOf = (err) => (err && typeof err === 'object' && typeof err.status === 'number' ? err.status : null);

/** 422 idempotency_key_reused: ключ уже создал запись, а тело другое — ничего не создано. */
export function isKeyReused(err) {
  return statusOf(err) === 422 && codeOf(err) === 'idempotency_key_reused';
}

/** Запись первой попытки из отказа 422 (`existing`); нет её — null. */
export function reusedExisting(err) {
  if (!err || typeof err !== 'object') return null;
  if (err.existing !== undefined && err.existing !== null) return err.existing;
  const d = err.data;
  return isObject(d) && d.existing != null ? d.existing : null;
}

/** 410 idempotency_object_gone: запись первой попытки удалена — следующей попытке новый ключ. */
export function isKeyGone(err) {
  return statusOf(err) === 410 && codeOf(err) === 'idempotency_object_gone';
}

/** Исход создания С КЛЮЧОМ неизвестен — правила isWriteOutcomeUnknown (lib/table/mutationQueue.js,
 *  S30 п. 5 а; тест сверяет оба на одних случаях): ответа нет (TypeError, AbortError, статус 0),
 *  408 и любой 5xx, включая 500 — ответ создания сериализуется после commit. Офлайн до отправки
 *  (notSent) — исход известен: запрос не ушёл. 4xx и ошибки без статуса — отказ. */
export function isKeyedCreateUnknown(err) {
  if (err && err.notSent) return false;
  if (err instanceof TypeError || (err && err.name === 'AbortError')) return true;
  const s = statusOf(err);
  if (s == null) return false;
  return s === 0 || s === 408 || s >= 500;
}

/* Пауза перед повтором по умолчанию — в счёте «в полёте» (client.trackedPause, S10): попытка ещё идёт,
   и переход вкладки в пространство, выбранное в соседней, ждёт её конца, а не уводит экран посреди
   паузы. Тесты подставляют свой sleep. */
const defaultSleep = (ms) => trackedPause(ms);

/** Попытка с ключом: run(0); исход неизвестен (isUnknown) — пауза delayMs и ровно ОДИН повтор
 *  run(1, отказПервой) тем же ключом (ключ держит вызывающий); его результат — наверх, его отказ —
 *  наверх как есть. Известный исход (4xx, 429, офлайн до отправки) — наверх сразу, без паузы.
 *  run обязан БРОСАТЬ отказ (unwrap конверта apiFetch — внутри run): конверт { ok:false }, который
 *  run вернул, — это ответ, повтора не будет. Второй аргумент повтора — отказ первой попытки:
 *  вызывающий, у которого перед повтором пропала сеть, бросает его же — исход первой попытки так и
 *  неизвестен (а не «не отправлено»). */
export async function retryOnceIfUnknown(run, { delayMs = RETRY_DELAY_MS, sleep = defaultSleep, isUnknown = isKeyedCreateUnknown } = {}) {
  let first;
  try {
    return await run(0);
  } catch (err) {
    if (!isUnknown(err)) throw err;
    first = err;
  }
  await sleep(delayMs);
  return run(1, first);
}
